import { Fragment, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  errorToast,
  getFilterKey,
  getLocalStorageItem,
  successToast,
  useOutsideClick
} from '../../../../../utils/helper';
import { Api } from '../../../../../api';
import { Helmet } from 'react-helmet';
import Breadcrumb from '../../../../../component/common/Breadcrumb';
import SelectMenu from '../../../../../component/common/SelectMenu';
import SearchInput from '../../../../../component/common/Input/SearchInput';
import { FunnelIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Transition } from '@headlessui/react';
import {
  PER_PAGE,
  STATUS_FOR_DROPDOWN,
  USER_TYPE,
  USER_TYPE_B2B
} from '../../../../../utils/constants';
import Table from '../../../../../component/common/Table';
import Pagination from '../../../../../component/common/Pagination/Pagination';
import Loader from '../../../../../component/common/Loader';
import Topbar from '../../../../PulseSurvey/Topbar';
import { PartnersIntroducedPageData } from '../../../../../utils/pageContent';
import { FilterPanel, TableCard} from '../../../../../component/common/designSystem';

function PartnerList({topbarShow = true}) {
  const navigate = useNavigate();
  const wrapperRef = useRef(null);
  const userData = getLocalStorageItem('userData') && JSON.parse(getLocalStorageItem('userData'));

  const pages = [
    {
      name: 'Partners introduced',
      href: '/partner-introduced',
      current: true
    }
  ];


  const [loader, setLoader] = useState(true);
  const [adminList, setAdminList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPerPage, setSelectedPerPage] = useState(PER_PAGE[0]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState(-1);
  const [sortBy, setSortBy] = useState('');
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filterQuery, setFilterQuery] = useState('');
  const [filterCriteria, setFilterCriteria] = useState({
    userType: '',
    accountStatus: ''
  });

  const handlePagination = (pageNumber, pageSize, searchKey, query, sortBy, sortOrder) => {
    setLoader(true);
    Api.getIntroduceCompanies(pageNumber, pageSize, searchKey, query, sortBy, sortOrder).then(
      (response) => {
        if (response?.data?.meta?.code === 1) {
          setCurrentPage(pageNumber);
          setAdminList(response?.data?.data);
          setTotalCount(response?.data?.meta?.totalRecords);
          setLoader(false);
        } else if (response?.code === 401) {
          setLoader(false);
          errorToast(response?.message);
        } else if (response?.data?.meta?.code === 0) {
          setCurrentPage(1);
          setAdminList([]);
          setTotalCount(0);
          setLoader(false);
          errorToast(response?.data?.meta?.message);
        } else {
          setLoader(false);
        }
      }
    );
  };

  useEffect(() => {
    if (searchTerm) {
      let searchParam = searchTerm.trim();
      const delayDebounceFn = setTimeout(() => {
        handlePagination(1, selectedPerPage?.value, searchParam, filterQuery, sortBy, sortOrder);
      }, 800);

      return () => clearTimeout(delayDebounceFn);
    } else {
      handlePagination(1, selectedPerPage?.value, '', '', sortBy, sortOrder);
    }
  }, [searchTerm]);

  const deleteHandler = (id) => {
    setLoader(true);
    setSearchTerm('');

    Api.deleteAdmin(id).then((response) => {
      if (response?.data?.meta?.code === 1) {
        handlePagination(1, selectedPerPage?.value, '', filterQuery, sortBy, sortOrder);
        successToast(response?.data?.meta?.message);
      } else if (response?.data?.meta?.code === 0) {
        setLoader(false);
        errorToast(response?.data?.meta?.message);
      } else {
        setLoader(false);
      }
    });
  };

  const handlePerPage = (perPage) => {
    setSelectedPerPage(perPage);
    handlePagination(1, perPage.value, searchTerm, filterQuery, sortBy, sortOrder);
  };

  useOutsideClick(wrapperRef, () => {
    if (showFilterModal) setShowFilterModal(!showFilterModal);
  });

  const filterHandler = (data, key) => {
    setFilterCriteria((prevData) => ({
      ...prevData,
      [key]: data
    }));
    let clone = JSON.parse(JSON.stringify(filterCriteria));
    const newData = { ...clone, [key]: data };
    let tempFilterQuery = [];
    Object.keys(newData).forEach(function (key) {
      if (newData[key]?.value?.toString()) {
        tempFilterQuery.push(`&${key}=${newData[key]?.value}`);
      }
    });
    let query = tempFilterQuery?.toString()?.replaceAll(',', '');
    setFilterQuery(query);
    handlePagination(1, selectedPerPage.value, searchTerm, query, sortBy, sortOrder);
  };

  const resetHandler = () => {
    setFilterCriteria({
      accountStatus: ''
    });
    setFilterQuery('');
    setShowFilterModal(false);
    handlePagination(1, selectedPerPage.value, searchTerm, '', sortBy, sortOrder);
  };

  const handleSortBy = (sortByValue) => {
    setSortBy(sortByValue);
    if (sortByValue === sortBy) {
      let tempSortOrder = sortOrder === 1 ? -1 : 1;
      setSortOrder(tempSortOrder);
      handlePagination(
        currentPage,
        selectedPerPage?.value,
        searchTerm,
        filterQuery,
        sortByValue,
        tempSortOrder
      );
    } else {
      setSortOrder(1);
      handlePagination(
        currentPage,
        selectedPerPage?.value,
        searchTerm,
        filterQuery,
        sortByValue,
        1
      );
    }
  };

  const refreshTable = () => {
    handlePagination(
      1,
      selectedPerPage?.value,
      "",
      filterQuery,
      sortBy,
      sortOrder
    );
  };

  return (
    <div className="relative lg:mb-[45px]">
      <Helmet>
        <meta charSet="utf-8" />
        <title>{PartnersIntroducedPageData.metaTitle}</title>
      </Helmet>
      {loader ? <Loader /> : null}
      {topbarShow === true &&
      <Topbar title={PartnersIntroducedPageData.topbarTitleText} />}
      <Breadcrumb pageList={pages} className="lg:mt-8" />

      <div className="mt-6">
        <div className="flex flex-wrap justify-between gap-2">
          <div className="flex">
            <div className="hidden lg:flex mr-3 self-center">
              <span className="self-center text-sm mr-2 text-shoorah-lightBlack dark:text-white">
                {PartnersIntroducedPageData.paginationSupportText}
              </span>
              <div className="w-[80px]">
                <SelectMenu
                  menuList={PER_PAGE}
                  showLabel={false}
                  defaultSelected={selectedPerPage}
                  setSelectedMenu={handlePerPage}
                />
              </div>
            </div>
            <div className="self-center">
              <SearchInput
                id="searchKey"
                name="searchKey"
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={PartnersIntroducedPageData.searchPlaceholder}
              />
            </div>
          </div>

          <div className="flex flex-wrap sm:gap-0 gap-3 justify-between">
            <div className="flex lg:hidden self-center">
              <div className="w-[80px]">
                <SelectMenu
                  menuList={PER_PAGE}
                  showLabel={false}
                  defaultSelected={selectedPerPage}
                  setSelectedMenu={handlePerPage}
                />
              </div>
            </div>

            <div className="flex flex-wrap">
              <div className="sm:ml-3 self-center inline-flex flex-wrap items-center justify-center">
                <button
                  className="btn-primary sm:w-auto"
                  onClick={() => navigate('/partners/add-partner')}
                >
                  {PartnersIntroducedPageData.addNewPartnerBtnText}
                </button>

                <button
                  className="btn-primary ml-3 sm:w-auto"
                  onClick={() => setShowFilterModal((state) => !state)}
                >
                  <FunnelIcon className="text-white w-[18px] h-[20px] inline" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {showFilterModal && (
          <FilterPanel
          open={showFilterModal}
          onClose={() => setShowFilterModal(false)}
          onReset={resetHandler}
        >
                <div>
                  <SelectMenu
                    menuList={STATUS_FOR_DROPDOWN}
                    label={PartnersIntroducedPageData.filtersModal.selectMenuOne.label}
                    isRequired={false}
                    defaultSelected={filterCriteria.accountStatus}
                    setSelectedMenu={(data) => filterHandler(data, 'accountStatus')}
                  />
                </div>
                {/* Add more filter options as needed */}
                <div>
                  <SelectMenu
                    menuList={PartnersIntroducedPageData.filtersModal.selectMenuTwo.menuItems}
                    label={PartnersIntroducedPageData.filtersModal.selectMenuTwo.label}
                    isRequired={false}
                    defaultSelected={filterCriteria.partnershipStatus}
                    setSelectedMenu={(data) => filterHandler(data, 'partnershipStatus')}
                  />
                </div>
              </FilterPanel>
        )}

        

        <TableCard
          pagination={{
            currentPage: currentPage,
            totalCount: totalCount,
            pageSize: selectedPerPage?.value,
            onPageChange: (page) =>
              handlePagination(
                page,
                selectedPerPage?.value,
                searchTerm,
                filterQuery,
                sortBy,
                sortOrder
              ),
            onPageSizeChange: (size) =>
              handlePerPage(PER_PAGE.find((o) => o.value === size) || PER_PAGE[0]),
            pageSizeOptions: PER_PAGE.map((o) => o.value)
          }}
        >
          <Table
            columns={PartnersIntroducedPageData.tableColumns}
            data={adminList}
            setDeleteId={deleteHandler}
            name={'partner_table'}
            bottomBorder={totalCount > selectedPerPage?.value}
            setSortBy={(sort) => handleSortBy(sort)}
            refreshTable={refreshTable}
            loader={loader}
            setSearchTerm={(data) => setSearchTerm(data)}
            isname={true}
          />
        </TableCard>
      </div>

      
    </div>
  );
}

export default PartnerList;